import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
  slug: "", title_tr: "", title_en: "", summary_tr: "", summary_en: "",
  description_tr: "", description_en: "", icon: "scale", order: 0,
};

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/services").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => { setEditing(item); setForm(item ? { ...item } : empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing && editing.id) {
        await api.patch(`/admin/services/${editing.id}`, form);
        toast.success("Hizmet güncellendi");
      } else {
        await api.post("/admin/services", form);
        toast.success("Hizmet eklendi");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/services/${id}`);
    toast.success("Silindi");
    load();
  };

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-services-page" className="p-10">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline text-[#4A6A8F]">İçerik</div>
          <h1 className="font-serif-ap text-4xl text-[#0F2746] mt-3">Hizmet Alanları</h1>
        </div>
        <button
          data-testid="add-service-btn"
          onClick={() => open(null)}
          className="bg-[#0F2746] text-[#F3F5F8] hover:bg-[#081A33] px-6 py-3 text-xs uppercase tracking-wider flex items-center gap-2"
        >
          <Plus size={14} /> Yeni Hizmet
        </button>
      </div>

      <div className="mt-10 bg-white border border-[#0F2746]/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F2746]/5 text-[#0F2746] text-left">
            <tr>
              <th className="p-4 overline text-[10px]">Sıra</th>
              <th className="p-4 overline text-[10px]">Başlık (TR)</th>
              <th className="p-4 overline text-[10px]">Title (EN)</th>
              <th className="p-4 overline text-[10px]">Slug</th>
              <th className="p-4 overline text-[10px] text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`service-row-${s.id}`} className="border-t border-[#0F2746]/10">
                <td className="p-4 font-serif-ap text-[#C4A47C]">{s.order}</td>
                <td className="p-4 text-[#0F2746]">{s.title_tr}</td>
                <td className="p-4 text-[#3A5170]">{s.title_en}</td>
                <td className="p-4 text-[#3A5170] text-xs font-mono">{s.slug}</td>
                <td className="p-4 text-right">
                  <button data-testid={`edit-service-${s.id}`} onClick={() => open(s)} className="text-[#0F2746] hover:text-[#C4A47C] p-2"><Pencil size={14} /></button>
                  <button data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)} className="text-[#0F2746] hover:text-red-600 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-[#081A33]/60 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto p-8 relative">
            <button type="button" onClick={close} className="absolute top-4 right-4 text-[#0F2746]"><X size={18} /></button>
            <h2 className="font-serif-ap text-3xl text-[#0F2746]">{editing.id ? "Hizmet Düzenle" : "Yeni Hizmet"}</h2>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <Input label="Slug" value={form.slug} onChange={onChange("slug")} required />
              <Input label="Sıra" type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
              <Input label="Başlık (TR)" value={form.title_tr} onChange={onChange("title_tr")} required />
              <Input label="Title (EN)" value={form.title_en} onChange={onChange("title_en")} required />
              <Textarea label="Özet (TR)" value={form.summary_tr} onChange={onChange("summary_tr")} required />
              <Textarea label="Summary (EN)" value={form.summary_en} onChange={onChange("summary_en")} required />
              <Textarea label="Açıklama (TR)" value={form.description_tr} onChange={onChange("description_tr")} />
              <Textarea label="Description (EN)" value={form.description_en} onChange={onChange("description_en")} />
              <Input label="İkon" value={form.icon} onChange={onChange("icon")} />
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button type="button" onClick={close} className="border border-[#0F2746]/30 text-[#0F2746] px-6 py-3 text-xs uppercase tracking-wider">İptal</button>
              <button type="submit" data-testid="save-service-btn" className="bg-[#0F2746] text-[#F3F5F8] hover:bg-[#081A33] px-6 py-3 text-xs uppercase tracking-wider">Kaydet</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, ...p }) {
  return (
    <label className="block">
      <span className="overline text-[10px] text-[#4A6A8F] block mb-2">{label}</span>
      <input {...p} className="w-full bg-transparent border-b border-[#0F2746]/30 focus:border-[#0F2746] outline-none py-2 text-[#0F2746]" />
    </label>
  );
}
function Textarea({ label, ...p }) {
  return (
    <label className="block col-span-2">
      <span className="overline text-[10px] text-[#4A6A8F] block mb-2">{label}</span>
      <textarea {...p} rows={3} className="w-full bg-transparent border border-[#0F2746]/30 focus:border-[#0F2746] outline-none p-3 text-[#0F2746] resize-none" />
    </label>
  );
}
